import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Quote, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const Stars = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type={onChange ? "button" : undefined} onClick={onChange ? () => onChange(n) : undefined}
        className={onChange ? "cursor-pointer" : "cursor-default"} data-testid={onChange ? `star-${n}` : undefined} aria-label={`${n} étoiles`}>
        <Star size={onChange ? 26 : 16} className={n <= value ? "fill-olive text-olive" : "text-line"} />
      </button>
    ))}
  </div>
);

export const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", commune: "", rating: 0, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.rating || !form.message) { toast.error("Merci de remplir votre nom, une note et votre avis."); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Merci ! Votre avis sera publié après validation.");
      setForm({ name: "", commune: "", rating: 0, message: "" });
      setOpen(false);
    } catch { toast.error("Une erreur est survenue."); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full rounded-xl border border-line bg-white px-4 py-3 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-olive focus:ring-offset-2 focus:ring-offset-sand transition-all";

  return (
    <section id="avis" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.4em] text-olive font-semibold">Avis clients</span>
          <h2 className="mt-5 font-display text-4xl md:text-5xl text-forest tracking-tight">Ils nous ont fait confiance</h2>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="leave-review-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-forest text-forest px-6 py-3 font-semibold hover:bg-forest hover:text-sand transition-all self-start">
          Laisser un avis
        </button>
      </Reveal>

      {open && (
        <Reveal className="mt-8">
          <form onSubmit={submit} data-testid="review-form" className="bg-white border border-line rounded-2xl p-6 md:p-8 grid gap-4 max-w-2xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Votre nom *" />
              <input data-testid="review-commune" value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })} className={inputCls} placeholder="Votre commune" />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-ink-soft">Votre note :</span>
              <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
            </div>
            <textarea data-testid="review-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className={inputCls + " resize-none"} placeholder="Votre avis *" />
            <button type="submit" disabled={submitting} data-testid="review-submit" className="inline-flex items-center justify-center gap-2 bg-olive text-white rounded-full px-7 py-3 font-semibold hover:bg-olive-hover transition-all disabled:opacity-60 w-fit">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />} Envoyer mon avis
            </button>
            <p className="text-xs text-ink-soft">Votre avis est vérifié avant publication afin de garantir l'authenticité des retours.</p>
          </form>
        </Reveal>
      )}

      {reviews.length > 0 ? (
        <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <StaggerItem key={r.id} className="bg-white border border-line rounded-2xl p-8 hover:shadow-soft transition-all" data-testid="review-card">
              <Quote className="text-olive/40" size={28} />
              <Stars value={r.rating} />
              <p className="mt-4 text-ink leading-relaxed">« {r.message} »</p>
              <p className="mt-5 text-sm font-semibold text-forest">{r.name}{r.commune ? ` — ${r.commune}` : ""}</p>
            </StaggerItem>
          ))}
        </Stagger>
      ) : (
        <Reveal className="mt-12 bg-sand-100 border border-line rounded-2xl p-10 text-center">
          <Quote className="text-olive/40 mx-auto" size={32} />
          <p className="mt-4 text-ink-soft max-w-lg mx-auto">Soyez le premier à partager votre expérience avec Sandro Services 06. Vos retours nous aident à progresser.</p>
        </Reveal>
      )}
    </section>
  );
};
